/**
 * TranscriptWidget Component
 * 
 * Unified transcript display with two modes:
 * - "Current" tab: Shows current segment synchronized with video playback
 * - "Full" tab: Shows all available transcripts in a scrollable list
 * 
 * Features:
 * - Tab-based navigation between Current and Full transcript views
 * - Clickable timecodes styled as buttons with play icons
 * - Clean, organized layout with subtle dividers
 * - Word-by-word interaction and translation support
 * 
 * Used in: VideoPostViewer
 */
import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    List,
    ListItem,
    Divider,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import { Subtitles, PlayArrow, Translate, MyLocation } from '@mui/icons-material';
import VideoCaptionInteractiveTextViewer from '../InteractiveText/VideoCaptionInteractiveTextViewer';

// Helper function to format time for display
const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Timecode button component
const TimecodeButton = ({ start, end, onSeekTo, isActive = false }) => {
    const timeLabel = `${formatTime(start)}–${formatTime(end)}`;
    
    return (
        <Chip
            icon={<PlayArrow sx={{ fontSize: 14 }} />}
            label={timeLabel}
            onClick={() => onSeekTo && onSeekTo(start)}
            sx={{
                cursor: onSeekTo ? 'pointer' : 'default',
                backgroundColor: isActive ? 'primary.main' : 'grey.100',
                color: isActive ? 'white' : 'text.primary',
                fontWeight: 500,
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                height: '28px',
                '&:hover': {
                    backgroundColor: onSeekTo 
                        ? (isActive ? 'primary.dark' : 'grey.200') 
                        : 'inherit',
                    transform: onSeekTo ? 'scale(1.05)' : 'none',
                },
                transition: 'all 0.2s ease',
                '& .MuiChip-icon': {
                    color: isActive ? 'white' : 'primary.main',
                    fontSize: '14px'
                }
            }}
        />
    );
};

const TranscriptWidget = ({ 
    currentTime = 0, 
    transcripts = [],
    onSeekTo = null,
    isShorts = false,
    onWordDisplayed = null
}) => {
    const [activeTab, setActiveTab] = useState(0);
    const [showAllTranslations, setShowAllTranslations] = useState(false);
    const fullTranscriptScrollRef = useRef(null);
    const currentTranscriptRef = useRef(null);

    // Find the current transcript based on time
    const currentTranscript = transcripts.find(
        transcript => currentTime >= transcript.start && currentTime < transcript.end
    );

    // Sort transcripts by start time for Full tab
    const sortedTranscripts = [...transcripts].sort((a, b) => a.start - b.start);

    // Prepare the text for InteractiveTextViewer
    const getTranscriptText = (transcript) => {
        if (!transcript) return '';
        return transcript.formattedText || transcript.text?.text || transcript.text || '';
    };

    // Get the text ID for translation requests
    const getTranscriptTextId = (transcript) => {
        if (!transcript) return '';
        return transcript.textId || transcript.text?._id || '';
    };

    // Get the startCount for word indexing
    const getTranscriptStartCount = (transcript) => {
        return transcript.startCount || 0;
    };

    // Get the whole text translation for the transcript
    const getTranscriptWholeTranslation = (transcript) => {
        return transcript.wholeTextTranslation || null;
    };

    // Check if transcript is currently active
    const isCurrentTranscript = (transcript) => {
        return currentTime >= transcript.start && currentTime < transcript.end;
    };

    // Scroll to current transcript in full view
    const scrollToCurrentTranscript = () => {
        if (currentTranscriptRef.current && fullTranscriptScrollRef.current) {
            currentTranscriptRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    };

    const handleWordDisplayed = (word) => {
        if (onWordDisplayed) {
            onWordDisplayed(word);
        }
    };

    // Render current segment view
    const renderCurrentView = () => {
        return (
            <Box sx={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: 3,
                backgroundColor: 'background.paper'
            }}>
                {currentTranscript ? (
                    <>
                        {/* Timecode button */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <TimecodeButton
                                start={currentTranscript.start}
                                end={currentTranscript.end}
                                onSeekTo={onSeekTo}
                                isActive={true}
                            />
                        </Box>
                        
                        {/* Transcript content */}
                        <Box sx={{ width: '100%' }}>
                            <VideoCaptionInteractiveTextViewer
                                formattedText={getTranscriptText(currentTranscript)}
                                textId={getTranscriptTextId(currentTranscript)}
                                startCount={getTranscriptStartCount(currentTranscript)}
                                wholeTextTranslation={getTranscriptWholeTranslation(currentTranscript)}
                                baseClassName="caption-interactive-text"
                                style={{
                                    lineHeight: 1.6,
                                    fontSize: '1.2rem',
                                    fontWeight: 400,
                                    color: 'inherit',
                                    textAlign: 'center',
                                    fontFamily: 'Georgia, serif'
                                }}
                                showInteractiveStyles={true}
                                source={{
                                    type: 'video_transcript',
                                    startTime: currentTranscript.start,
                                    endTime: currentTranscript.end
                                }}
                                onWordDisplayed={handleWordDisplayed}
                                enableTranslation={true}
                            />
                        </Box>
                        
                        {/* Progress Bar for Current Caption */}
                        <Box sx={{ mt: 2, px: 1 }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '4px',
                                    backgroundColor: 'grey.200',
                                    borderRadius: '2px',
                                    overflow: 'hidden'
                                }}
                            >
                                <Box
                                    sx={{
                                        height: '100%',
                                        backgroundColor: 'primary.main',
                                        borderRadius: '2px',
                                        transition: 'width 0.1s ease',
                                        width: `${Math.min(100, Math.max(0, 
                                            ((currentTime - currentTranscript.start) / 
                                            (currentTranscript.end - currentTranscript.start)) * 100
                                        ))}%`
                                    }}
                                />
                            </Box>
                        </Box>
                    </>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Subtitles sx={{ 
                            fontSize: 48, 
                            color: 'grey.300', 
                            mb: 2 
                        }} />
                        <Typography 
                            variant="body1" 
                            sx={{ color: 'text.secondary', fontWeight: 400 }}
                        >
                            No caption at current time
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    // Render full transcript view
    const renderFullView = () => {
        return (
            <Box 
                ref={fullTranscriptScrollRef}
                sx={{ 
                    flex: 1, 
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    minHeight: 0, // Important for flex scrolling
                    maxHeight: '100%',
                    position: 'relative'
                }}
            >
                {sortedTranscripts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Subtitles sx={{ 
                            fontSize: 48, 
                            color: 'grey.300', 
                            mb: 2 
                        }} />
                        <Typography 
                            variant="body1" 
                            sx={{ color: 'text.secondary' }}
                        >
                            No captions available
                        </Typography>
                    </Box>
                ) : (
                    <List sx={{ p: 0 }}>
                        {sortedTranscripts.map((transcript, index) => {
                            const isActive = isCurrentTranscript(transcript);
                            
                            return (
                                <React.Fragment key={`${transcript.textId}-${index}`}>
                                    <ListItem 
                                        ref={isActive ? currentTranscriptRef : null}
                                        sx={{ 
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                            py: 2,
                                            px: 2,
                                            backgroundColor: isActive ? 'primary.50' : 'transparent',
                                            borderLeft: isActive ? '4px solid' : 'none',
                                            borderColor: isActive ? 'primary.main' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: isActive ? 'primary.100' : 'action.hover'
                                            }
                                        }}
                                    >
                                        {/* Timecode button - left aligned */}
                                        <Box sx={{ mb: 1.5 }}>
                                            <TimecodeButton
                                                start={transcript.start}
                                                end={transcript.end}
                                                onSeekTo={onSeekTo}
                                                isActive={isActive}
                                            />
                                        </Box>
                                        
                                        {/* Transcript content */}
                                        <Box sx={{ width: '100%' }}>
                                            <VideoCaptionInteractiveTextViewer
                                                formattedText={getTranscriptText(transcript)}
                                                textId={getTranscriptTextId(transcript)}
                                                startCount={getTranscriptStartCount(transcript)}
                                                wholeTextTranslation={getTranscriptWholeTranslation(transcript)}
                                                baseClassName={`caption-list-${index}`}
                                                style={{
                                                    lineHeight: 1.5,
                                                    fontSize: '0.95rem',
                                                    fontWeight: 400,
                                                    color: 'inherit',
                                                    textAlign: 'left',
                                                    fontFamily: 'Georgia, serif'
                                                }}
                                                showInteractiveStyles={true}
                                                source={{
                                                    type: 'video_transcript_list',
                                                    startTime: transcript.start,
                                                    endTime: transcript.end
                                                }}
                                                onWordDisplayed={handleWordDisplayed}
                                                enableTranslation={true}
                                                forceShowTranslation={showAllTranslations}
                                            />
                                        </Box>
                                    </ListItem>
                                    
                                    {index < sortedTranscripts.length - 1 && (
                                        <Divider sx={{ mx: 2 }} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100%',
            width: '100%',
            maxHeight: 'inherit'
        }}>
            <Paper 
                sx={{ 
                    flex: 1,
                    backgroundColor: 'background.paper',
                    color: 'text.primary',
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 2,
                    minHeight: 0, // Important for flex scrolling
                    height: '100%',
                    maxHeight: '100%',
                    position: 'relative'
                }}
            >
                {/* Header with Tabs */}
                <Box sx={{ 
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'grey.50',
                    flexShrink: 0
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        px: 2, 
                        pt: 2,
                        pb: 0
                    }}>
                        <Subtitles sx={{ fontSize: 20, mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Transcript
                        </Typography>
                        {sortedTranscripts.length > 0 && (
                            <Chip 
                                label={sortedTranscripts.length} 
                                size="small" 
                                sx={{ ml: 1, height: '20px' }}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => setActiveTab(newValue)}
                            sx={{
                                flex: 1,
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    minHeight: 48
                                }
                            }}
                        >
                            <Tab label="Current" />
                            <Tab label="Full transcript" />
                        </Tabs>
                        {activeTab === 1 && sortedTranscripts.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, pr: 1 }}>
                                <Tooltip title={showAllTranslations ? "Hide all translations" : "Show all translations"}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setShowAllTranslations(!showAllTranslations)}
                                        color={showAllTranslations ? "primary" : "default"}
                                    >
                                        <Translate fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Scroll to current transcript">
                                    <IconButton
                                        size="small"
                                        onClick={scrollToCurrentTranscript}
                                        color="primary"
                                    >
                                        <MyLocation fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Tab Content */}
                <Box sx={{ 
                    flex: 1, 
                    overflow: 'hidden', 
                    display: 'flex', 
                    flexDirection: 'column',
                    minHeight: 0 // Important for flex scrolling
                }}>
                    {activeTab === 0 ? renderCurrentView() : renderFullView()}
                </Box>
            </Paper>
        </Box>
    );
};

export default TranscriptWidget;

